import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { extractUpstreamCodes } from "@brewbase/lib";
import { trpc } from "@/hooks/trpc";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { UpstreamPicker } from "./UpstreamPicker";
import type { HarvestOperationTemplateRevisionId } from "@brewbase/db/types";

interface UpstreamChipsProps {
  templateId: HarvestOperationTemplateRevisionId;
  /** Current formula text (for solid-vs-muted visual state). */
  formula: string;
  /**
   * Called when the user clicks a declared (muted) chip to insert it into
   * the formula. The editor is responsible for inserting at the cursor.
   */
  onInsert?: (code: string) => void;
  disabled?: boolean;
}

export function UpstreamChips({
  templateId,
  formula,
  onInsert,
  disabled,
}: UpstreamChipsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const utils = trpc.useUtils();
  const { data: deps } = trpc.harvest.templateUpstreamDeps.list.useQuery({
    operation_template_id: templateId,
  });
  const remove = trpc.harvest.templateUpstreamDeps.remove.useMutation({
    onSuccess: () =>
      Promise.all([
        utils.harvest.templateUpstreamDeps.list.invalidate({
          operation_template_id: templateId,
        }),
        // Keep the formula context (diagnostics source) in sync with the
        // upstream allowlist.
        utils.formulas.getContext.invalidate(),
      ]),
  });

  const usedCodes = useMemo(() => {
    try {
      return extractUpstreamCodes(formula);
    } catch {
      return new Set<string>();
    }
  }, [formula]);

  const sortedDeps = useMemo(
    () => (deps ?? []).slice().sort((a, b) => a.code.localeCompare(b.code)),
    [deps],
  );

  async function handleRemove(code: string) {
    const result = await remove.mutateAsync({
      operation_template_id: templateId,
      code,
    });
    if (!result.ok) {
      toast.error(
        `Cannot remove '${code}' — still used by: ${result.blockedBy
          .map((u) => u.label)
          .join(", ")}`,
      );
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-1">
        {sortedDeps.map((dep) => {
          const isUsed = usedCodes.has(dep.code);
          return (
            <span
              key={dep.code}
              className={cn(
                "group inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono border",
                isUsed
                  ? "bg-[var(--farmless-green)] text-white border-[var(--farmless-green)]"
                  : "bg-transparent text-olive-11 border-olive-6 hover:border-olive-8",
                !isUsed && !disabled && onInsert && "cursor-pointer",
              )}
              title={dep.description ?? dep.code}
              onClick={() => {
                if (!disabled && !isUsed && onInsert) onInsert(dep.code);
              }}
            >
              <span>upstream.{dep.code}</span>
              <button
                type="button"
                className={cn(
                  "opacity-60 hover:opacity-100 transition-opacity",
                  disabled && "hidden",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleRemove(dep.code);
                }}
                aria-label={`Remove upstream dependency ${dep.code}`}
              >
                <X size={10} />
              </button>
            </span>
          );
        })}
        <button
          type="button"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono border border-dashed border-olive-6 text-olive-10 hover:border-olive-8 hover:text-olive-11"
          onClick={() => setPickerOpen(true)}
          disabled={disabled}
        >
          <Plus size={10} />
          <span>upstream</span>
        </button>
      </div>
      <UpstreamPicker
        templateId={templateId}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        existingCodes={new Set(sortedDeps.map((d) => d.code))}
      />
    </>
  );
}
